/**
 * Steers the continuous voices from sim state: the aircraft by phase and distance, the wind by
 * biome, weather and altitude, the mini-bunker's whine and the flamethrower's roar by whichever
 * is nearest, the siren by the alert, the hum by what is still standing. Runs at a 20 Hz control
 * rate inside `update` - WebAudio glides between the targets - and allocates nothing per frame.
 */

import { bunkerLive, weaponUp } from '../../sim/enemies/specials';
import type { Enemy, MissionDef, PropKind, World } from '../../sim/types';
import { type AircraftInput, aircraftMix, createAircraftMix, createWindMix, gustAt, windMix } from '../airMix';
import { sirenOn } from '../mix';
import type { Occlusion } from '../occlusion';
import { createSpatial, type Spatial, SPATIAL, spatialise } from '../spatial';
import { Ambience } from './ambience';
import type { Graph } from './graph';
import { FlameVoice, OspreyVoice, SirenVoice, WhineVoice } from './machines';

const CONTROL_SECONDS = 0.05;
const HUM_RANGE = 35;
const HUMMERS: ReadonlySet<PropKind> = new Set<PropKind>(['radar', 'pumpStation', 'commsPost', 'searchlight']);

interface Hummer {
  id: number;
  x: number;
  z: number;
  alive: boolean;
}

export class Loops {
  private readonly osprey: OspreyVoice;
  private readonly whine: WhineVoice;
  private readonly siren: SirenVoice;
  private readonly flame: FlameVoice;
  private readonly ambience: Ambience;
  private readonly aircraft = createAircraftMix();
  private readonly windOut = createWindMix();
  private readonly scratch = createSpatial();
  private readonly nearest = createSpatial();
  private readonly input: AircraftInput = {
    phase: 'ground', altitude: 0, osprey: 'away', ospreyTicks: 0, recalled: false, departedSeconds: 0,
    pickupX: 0, pickupZ: 0, listenerX: 0, listenerZ: 0,
  };
  private def: MissionDef | null = null;
  private hummers: Hummer[] = [];
  private accumulator = 0;
  private clock = 0;
  /** Seconds since the `siren` event; negative = no siren. */
  private sirenAge = -1;
  occlusion: Occlusion | null = null;

  constructor(graph: Graph) {
    this.osprey = new OspreyVoice(graph);
    this.whine = new WhineVoice(graph);
    this.siren = new SirenVoice(graph);
    this.flame = new FlameVoice(graph);
    this.ambience = new Ambience(graph);
  }

  begin(def: MissionDef): void {
    this.def = def;
    this.hummers = def.props.filter((p) => HUMMERS.has(p.kind)).map((p) => ({ id: p.id, x: p.x, z: p.z, alive: true }));
    this.sirenAge = -1;
    this.input.departedSeconds = 0;
    this.input.pickupX = def.pickup.x;
    this.input.pickupZ = def.pickup.z;
    this.accumulator = CONTROL_SECONDS;
  }

  sirenStarted(): void {
    this.sirenAge = 0;
  }

  propDestroyed(id: number): void {
    for (const h of this.hummers) if (h.id === id) h.alive = false;
  }

  end(): void {
    this.def = null;
    this.quiet();
  }

  /** Everything down: the mission ended, or the game stopped calling `update` (paused, tab hidden). */
  quiet(): void {
    this.aircraft.gain = 0;
    this.osprey.update(this.aircraft);
    this.whine.update(null);
    this.flame.update(null);
    this.siren.update(false);
    this.ambience.silence();
  }

  /** The nearest enemy passing `test`, spatialised into `this.nearest`; null if none is in earshot. */
  private nearestEnemy(world: World, test: (e: Enemy) => boolean, lx: number, lz: number): Spatial | null {
    let best = SPATIAL.maxDistance * SPATIAL.maxDistance;
    let found: Enemy | null = null;
    for (const e of world.enemies) {
      if (!test(e)) continue;
      const d = (e.x - lx) * (e.x - lx) + (e.z - lz) * (e.z - lz);
      if (d < best) {
        best = d;
        found = e;
      }
    }
    if (!found) return null;
    const occluded = this.occlusion?.blocked(lx, lz, found.x, found.z) ?? false;
    return spatialise(found.x - lx, found.z - lz, SPATIAL.maxDistance, occluded, this.nearest);
  }

  update(world: World, dt: number, lx: number, lz: number): void {
    const def = this.def;
    if (!def) return;
    this.clock += dt;
    if (this.sirenAge >= 0) this.sirenAge += dt;
    if (world.osprey.phase === 'departed') this.input.departedSeconds += dt;
    else this.input.departedSeconds = 0;
    this.accumulator += dt;
    if (this.accumulator < CONTROL_SECONDS) return;
    this.accumulator = 0;

    const i = this.input;
    i.phase = world.insertion.phase;
    i.altitude = world.insertion.altitude;
    i.osprey = world.osprey.phase;
    i.ospreyTicks = world.osprey.ticks;
    i.recalled = world.osprey.recalled;
    i.listenerX = lx;
    i.listenerZ = lz;
    this.osprey.update(aircraftMix(i, this.scratch, this.aircraft));

    const wind = windMix(def.biome, def.weather, i.phase, i.altitude, gustAt(this.clock), this.windOut);
    this.ambience.wind(wind, 1);

    this.whine.update(this.nearestEnemy(world, bunkerLive, lx, lz));
    this.flame.update(this.nearestEnemy(world, weaponUpFlame, lx, lz));
    this.siren.update(sirenOn(this.sirenAge, world.alert));

    let hum: Spatial | null = null;
    let best = HUM_RANGE * HUM_RANGE;
    for (const h of this.hummers) {
      if (!h.alive) continue;
      const d = (h.x - lx) * (h.x - lx) + (h.z - lz) * (h.z - lz);
      if (d < best) {
        best = d;
        hum = spatialise(h.x - lx, h.z - lz, HUM_RANGE, false, this.scratch);
      }
    }
    this.ambience.hum(i.phase === 'ground' ? hum : null);
  }
}

/** A lit flamethrower jet (`weaponUp` is also true of a rocket trooper taking aim, which is silent). */
function weaponUpFlame(e: Enemy): boolean {
  return e.kind === 'flamethrower' && weaponUp(e);
}
